import * as React from "react";
import { useCallback, useRef } from "react";
import { css } from "@emotion/core";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  ButtonGroup,
  InputGroup,
  HTMLSelect,
  Popover,
  Tag,
  MenuItem,
  MenuItemProps,
  Icon,
  Switch,
} from "@blueprintjs/core";
import {
  MultiSelect,
  ItemRenderer,
  ItemRendererProps,
} from "@blueprintjs/select";
import { DateRangeInput } from "@blueprintjs/datetime";
import { format } from "date-fns";
import CreateSurvey from "components/CreateSurvey";
import { useSinkState, useSinkActions } from "context/";
import type { SurveyDateType } from "context/sink";
import Survey from "models/Survey";
import { FoldersView, SurveysView } from "./ListView";
import { FolderAdminReactivatePopover } from "./FolderAdminReactivatePopover";
import { ITag } from "../../../types/Tag";
import { filterTag, areTagsEqual, renderTag } from "../../../utils/tags";
import {
  collectUniqueOrderTypeLabels,
  resolveSelectedOrderTypes,
} from "../../../utils/orderTypeLabel";
import { useTags } from "hooks/useTags";
import { useFilteredSurveys } from "../../../hooks/useFilteredSurveys";
import { useSurveyFilterParams } from "../../../hooks/useSurveyFilterParams";
import { useSelectedItems } from "../../../hooks/useSelectedItems";
import { cappedItemListRenderer } from "../../Admin/common/cappedItemListRenderer";

interface IStatus {
  value: string;
  label: string;
}

interface IOrderTypeOption {
  value: string;
  label: string;
}

interface IUser {
  id: number;
  name: string;
}

interface ISite {
  id: number;
  label: string;
}

interface IFolder {
  id: number;
  label: string;
}

const statusOptions: IStatus[] = [
  { value: "created", label: "No orders" },
  { value: "in-progress", label: "In-progress" },
  { value: "complete", label: "Complete" },
  { value: "locked", label: "Locked" },
];

const SURVEY_DATE_TYPE_LABELS: Record<SurveyDateType, string> = {
  "created-date": "Created",
  "flight-date": "Flight",
  "locked-date": "Locked",
  "submitted-date": "Submitted",
  "completed-date": "Completed",
};

const filterStatus = (query: string, status: IStatus) =>
  status.label.toLowerCase().includes(query.toLowerCase());

const areStatusesEqual = (status1: IStatus, status2: IStatus) =>
  status1.value === status2.value;

const renderStatus = (status: IStatus) => status.label;

const filterOrderType = (query: string, orderType: IOrderTypeOption) =>
  orderType.label.toLowerCase().includes(query.toLowerCase());

const areOrderTypesEqual = (
  orderType1: IOrderTypeOption,
  orderType2: IOrderTypeOption
) => orderType1.value === orderType2.value;

const renderOrderType = (orderType: IOrderTypeOption) => orderType.label;

const filterUser = (query: string, user: IUser) =>
  user.name.toLowerCase().includes(query.toLowerCase());

const areUsersEqual = (user1: IUser, user2: IUser) => user1.id === user2.id;

const renderUser = (user: IUser) => user.name;

const filterSite = (query: string, site: ISite) =>
  site.label.toLowerCase().includes(query.toLowerCase());

const areSitesEqual = (site1: ISite, site2: ISite) => site1.id === site2.id;

const renderSite = (site: ISite) => site.label;

const filterFolder = (query: string, folder: IFolder) =>
  folder.label.toLowerCase().includes(query.toLowerCase());

const areFoldersEqual = (folder1: IFolder, folder2: IFolder) =>
  folder1.id === folder2.id;

const renderFolder = (folder: IFolder) => folder.label;

const noResults = (
  <MenuItem disabled={true} text="No results." roleStructure="listoption" />
);

function FilterChip({
  header,
  onRemove,
  children,
}: {
  header: string;
  onRemove: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        text-align: left;
        background-color: var(--neutral-6);
        border-radius: 4px;
        border: 1px solid var(--neutral-6);
        border-top: none;
        min-width: 80px;
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 4px;
        `}
      >
        <span
          css={css`
            font-size: 8px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.05rem;
            color: var(--neutral-4);
            padding: 2px 0 2px 4px;
          `}
        >
          {header}
        </span>
        <button
          css={css`
            background: transparent;
            border: none;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 2px;
            margin: 0;
            color: var(--neutral-4);

            &:hover {
              color: var(--neutral-3);
            }
          `}
          onClick={onRemove}
        >
          <Icon icon="cross" size={10} />
        </button>
      </div>
      <div
        css={css`
          font-size: 10px;
          color: var(--neutral-3);
          background-color: var(--neutral-7);
          padding: 1px 6px 2px 4px;
          border-radius: 0 0 4px 4px;
        `}
      >
        {children}
      </div>
    </div>
  );
}

function getTagItemProps(
  tag: ITag,
  { handleClick, handleFocus, modifiers, ref }: ItemRendererProps,
  isSelected?: boolean
): MenuItemProps & React.Attributes {
  return {
    active: modifiers.active,
    disabled: modifiers.disabled,
    key: tag.id,
    onClick: handleClick,
    onFocus: handleFocus,
    ref,
    selected: isSelected,
    text: tag.label,
  };
}

function getStatusItemProps(
  status: IStatus,
  { handleClick, handleFocus, modifiers, ref }: ItemRendererProps,
  isSelected?: boolean
): MenuItemProps & React.Attributes {
  return {
    active: modifiers.active,
    disabled: modifiers.disabled,
    key: status.value,
    onClick: handleClick,
    onFocus: handleFocus,
    ref,
    selected: isSelected,
    text: status.label,
  };
}

function getOrderTypeItemProps(
  orderType: IOrderTypeOption,
  { handleClick, handleFocus, modifiers, ref }: ItemRendererProps,
  isSelected?: boolean
): MenuItemProps & React.Attributes {
  return {
    active: modifiers.active,
    disabled: modifiers.disabled,
    key: orderType.value,
    onClick: handleClick,
    onFocus: handleFocus,
    ref,
    selected: isSelected,
    text: orderType.label,
  };
}

function getUserItemProps(
  user: IUser,
  { handleClick, handleFocus, modifiers, ref }: ItemRendererProps,
  isSelected?: boolean
): MenuItemProps & React.Attributes {
  return {
    active: modifiers.active,
    disabled: modifiers.disabled,
    key: user.id,
    onClick: handleClick,
    onFocus: handleFocus,
    ref,
    selected: isSelected,
    text: user.name,
  };
}

function getSiteItemProps(
  site: ISite,
  { handleClick, handleFocus, modifiers, ref }: ItemRendererProps,
  isSelected?: boolean
): MenuItemProps & React.Attributes {
  return {
    active: modifiers.active,
    disabled: modifiers.disabled,
    key: site.id,
    onClick: handleClick,
    onFocus: handleFocus,
    ref,
    selected: isSelected,
    text: site.label,
  };
}

function getFolderItemProps(
  folder: IFolder,
  { handleClick, handleFocus, modifiers, ref }: ItemRendererProps,
  isSelected?: boolean
): MenuItemProps & React.Attributes {
  return {
    active: modifiers.active,
    disabled: modifiers.disabled,
    key: folder.id,
    onClick: handleClick,
    onFocus: handleFocus,
    ref,
    selected: isSelected,
    text: folder.label,
  };
}

const formatDateForDisplay = (dateString: string): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return format(date, "yyyy-MM-dd");
};

const getUsersFromSurveys = (surveys: Survey[]): IUser[] => {
  const userMap = new Map<number, string>();

  surveys.forEach((survey) => {
    const surveyAny = survey as any;
    // Add creator
    if (surveyAny.created_by && surveyAny.created_by_name) {
      userMap.set(surveyAny.created_by, surveyAny.created_by_name);
    }
    // Add locker
    if (surveyAny.locked_by_id && surveyAny.locked_by_name) {
      userMap.set(surveyAny.locked_by_id, surveyAny.locked_by_name);
    }
  });

  return Array.from(userMap.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

const getOrderTypesFromOrders = (
  orders: Array<{
    survey_id?: number | null;
    type: string;
    order_type_name?: string | null;
  }>
): IOrderTypeOption[] =>
  collectUniqueOrderTypeLabels(orders).map((label) => ({
    value: label,
    label,
  }));

const SurveysNavigator = () => {
  const { surveyListFilters, sink } = useSinkState();
  useSurveyFilterParams();
  const { surveys, folderFilteredSurveys, filteredSurveys, hasActiveFilters } =
    useFilteredSurveys();
  const { tags } = useTags();
  const sites = sink.sites || [];
  const folders = sink.folders || [];

  const {
    searchString: surveySearchString,
    selectedTagIds = [],
    selectedFolderIds = [],
    selectedSiteIds = [],
    selectedStatuses = [],
    selectedOrderTypes = [],
    dateRange: surveyDateRange,
    dateType: surveyDateType,
    selectedCreatedByUserIds = [],
    selectedLockedByUserIds = [],
    showOnlyArchived = false,
  } = surveyListFilters;
  const {
    setSurveySearchString,
    setSelectedTagIds,
    setSelectedFolderIds,
    setSelectedSiteIds,
    setSelectedStatuses,
    setSelectedOrderTypes,
    setSurveyDateRange,
    setSurveyDateType,
    setSelectedCreatedByUserIds,
    setSelectedLockedByUserIds,
    setShowOnlyArchived,
    expandAllSurveys,
    collapseAllSurveys,
    resink,
  } = useSinkActions();
  const [showCreateDialog, setShowCreateDialog] = React.useState(false);
  const [isFilterOpen, setIsFilterOpen] = React.useState(false);
  const visibleSurveyIdsRef = useRef<number[]>([]);
  const handleVisibleSurveyIdsChange = useCallback((ids: number[]) => {
    visibleSurveyIdsRef.current = ids;
  }, []);

  const params = useParams<{ id?: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const isFolderListView =
    location.pathname === "/folders" && !hasActiveFilters;

  const currentFolderId = params.id ? parseInt(params.id, 10) : null;
  const currentFolder = currentFolderId
    ? folders.find((f) => f.id === currentFolderId)
    : null;

  const availableUsers = React.useMemo(
    () => getUsersFromSurveys(surveys),
    [surveys]
  );

  const availableOrderTypes = React.useMemo(
    () => getOrderTypesFromOrders(sink.orders ?? []),
    [sink.orders]
  );

  const getSelectedTags = useCallback((tagIds: number[], allTags: ITag[]) => {
    if (!tagIds || !allTags) return [];
    return tagIds
      .map((tagId: number) => allTags.find((t) => t.id === tagId))
      .filter(Boolean) as ITag[];
  }, []);

  const handleTagRemove = useCallback(
    (tag: ITag) => {
      const newTagIds = selectedTagIds.filter((id: number) => id !== tag.id);
      setSelectedTagIds(newTagIds);
    },
    [selectedTagIds, setSelectedTagIds]
  );

  const handleTagSelect = useCallback(
    (tag: ITag) => {
      if (selectedTagIds.includes(tag.id)) {
        handleTagRemove(tag);
      } else {
        const newTagIds = [...selectedTagIds, tag.id];
        setSelectedTagIds(newTagIds);
      }
    },
    [selectedTagIds, setSelectedTagIds, handleTagRemove]
  );

  const handleTagClear = useCallback(() => {
    setSelectedTagIds([]);
  }, [setSelectedTagIds]);

  const renderTagItem: ItemRenderer<ITag> = useCallback(
    (tag, rendererProps) => {
      if (!rendererProps.modifiers.matchesPredicate) {
        return null;
      }
      const selectedTags = getSelectedTags(selectedTagIds, tags);
      const isSelected = selectedTags.some(
        (selectedTag: ITag) => selectedTag.id === tag.id
      );

      return (
        <MenuItem
          {...getTagItemProps(tag, rendererProps, isSelected)}
          roleStructure="listoption"
          shouldDismissPopover={false}
          text={tag.label}
        />
      );
    },
    [selectedTagIds, getSelectedTags, tags]
  );

  const getSelectedSites = useCallback(
    (siteIds: number[], allSites: ISite[]) => {
      if (!siteIds || !allSites) return [];
      return siteIds
        .map((siteId: number) => allSites.find((s) => s.id === siteId))
        .filter(Boolean) as ISite[];
    },
    []
  );

  const handleSiteRemove = useCallback(
    (site: ISite) => {
      const newSiteIds = selectedSiteIds.filter((id: number) => id !== site.id);
      setSelectedSiteIds(newSiteIds);
    },
    [selectedSiteIds, setSelectedSiteIds]
  );

  const handleSiteSelect = useCallback(
    (site: ISite) => {
      if (selectedSiteIds.includes(site.id)) {
        handleSiteRemove(site);
      } else {
        const newSiteIds = [...selectedSiteIds, site.id];
        setSelectedSiteIds(newSiteIds);
      }
    },
    [selectedSiteIds, setSelectedSiteIds, handleSiteRemove]
  );

  const handleSiteClear = useCallback(() => {
    setSelectedSiteIds([]);
  }, [setSelectedSiteIds]);

  const renderSiteItem: ItemRenderer<ISite> = useCallback(
    (site, rendererProps) => {
      if (!rendererProps.modifiers.matchesPredicate) {
        return null;
      }
      const selectedSitesList = getSelectedSites(selectedSiteIds, sites);
      const isSelected = selectedSitesList.some(
        (selectedSite: ISite) => selectedSite.id === site.id
      );

      return (
        <MenuItem
          {...getSiteItemProps(site, rendererProps, isSelected)}
          roleStructure="listoption"
          shouldDismissPopover={false}
          text={site.label}
        />
      );
    },
    [selectedSiteIds, getSelectedSites, sites]
  );

  const getSelectedFolders = useCallback(
    (folderIds: number[], allFolders: IFolder[]) => {
      if (!folderIds || !allFolders) return [];
      return folderIds
        .map((folderId: number) => allFolders.find((f) => f.id === folderId))
        .filter(Boolean) as IFolder[];
    },
    []
  );

  const handleFolderFilterRemove = useCallback(
    (folder: IFolder) => {
      const newFolderIds = selectedFolderIds.filter(
        (id: number) => id !== folder.id
      );
      setSelectedFolderIds(newFolderIds);
    },
    [selectedFolderIds, setSelectedFolderIds]
  );

  const handleFolderSelect = useCallback(
    (folder: IFolder) => {
      if (folder.id === currentFolderId) {
        navigate({ pathname: "/surveys", search: location.search });
        return;
      }
      if (selectedFolderIds.includes(folder.id)) {
        handleFolderFilterRemove(folder);
      } else {
        const newFolderIds = [...selectedFolderIds, folder.id];
        setSelectedFolderIds(newFolderIds);
      }
    },
    [
      selectedFolderIds,
      setSelectedFolderIds,
      handleFolderFilterRemove,
      currentFolderId,
      navigate,
    ]
  );

  const handleFolderClear = useCallback(() => {
    setSelectedFolderIds([]);
  }, [setSelectedFolderIds]);

  const renderFolderItem: ItemRenderer<IFolder> = useCallback(
    (folder, rendererProps) => {
      if (!rendererProps.modifiers.matchesPredicate) {
        return null;
      }
      const selectedFoldersList = getSelectedFolders(
        selectedFolderIds,
        folders
      );
      const isSelected =
        selectedFoldersList.some(
          (selectedFolder: IFolder) => selectedFolder.id === folder.id
        ) || folder.id === currentFolderId;

      return (
        <MenuItem
          {...getFolderItemProps(folder, rendererProps, isSelected)}
          roleStructure="listoption"
          shouldDismissPopover={false}
          text={folder.label}
        />
      );
    },
    [selectedFolderIds, getSelectedFolders, folders, currentFolderId]
  );

  const getSelectedStatuses = useCallback(
    (statusValues: string[], allStatuses: IStatus[]) => {
      if (!statusValues || !allStatuses) return [];
      return statusValues
        .map((statusValue: string) =>
          allStatuses.find((s) => s.value === statusValue)
        )
        .filter(Boolean) as IStatus[];
    },
    []
  );

  const handleStatusRemove = useCallback(
    (status: IStatus) => {
      const newStatusValues = selectedStatuses.filter(
        (value: string) => value !== status.value
      );
      setSelectedStatuses(newStatusValues);
    },
    [selectedStatuses, setSelectedStatuses]
  );

  const handleStatusSelect = useCallback(
    (status: IStatus) => {
      if (selectedStatuses.includes(status.value)) {
        handleStatusRemove(status);
      } else {
        const newStatusValues = [...selectedStatuses, status.value];
        setSelectedStatuses(newStatusValues);
      }
    },
    [selectedStatuses, setSelectedStatuses, handleStatusRemove]
  );

  const handleStatusClear = useCallback(() => {
    setSelectedStatuses([]);
  }, [setSelectedStatuses]);

  const getSelectedOrderTypes = useCallback(
    (orderTypeValues: string[], allOrderTypes: IOrderTypeOption[]) =>
      resolveSelectedOrderTypes(orderTypeValues, allOrderTypes),
    []
  );

  const handleOrderTypeRemove = useCallback(
    (orderType: IOrderTypeOption) => {
      const newOrderTypeValues = selectedOrderTypes.filter(
        (value: string) => value !== orderType.value
      );
      setSelectedOrderTypes(newOrderTypeValues);
    },
    [selectedOrderTypes, setSelectedOrderTypes]
  );

  const handleOrderTypeSelect = useCallback(
    (orderType: IOrderTypeOption) => {
      if (selectedOrderTypes.includes(orderType.value)) {
        handleOrderTypeRemove(orderType);
      } else {
        const newOrderTypeValues = [...selectedOrderTypes, orderType.value];
        setSelectedOrderTypes(newOrderTypeValues);
      }
    },
    [selectedOrderTypes, setSelectedOrderTypes, handleOrderTypeRemove]
  );

  const handleOrderTypeClear = useCallback(() => {
    setSelectedOrderTypes([]);
  }, [setSelectedOrderTypes]);

  const handleSearchRemove = useCallback(() => {
    setSurveySearchString(null);
  }, [setSurveySearchString]);

  const handleFolderRemove = useCallback(() => {
    navigate({ pathname: "/folders", search: location.search });
  }, [navigate, location.search]);

  const handleDateRangeRemove = useCallback(() => {
    setSurveyDateRange({ startDate: "", endDate: "" });
  }, [setSurveyDateRange]);

  const handleDateRangeChange = useCallback(
    (dateRange: [Date | null, Date | null]) => {
      const [startDate, endDate] = dateRange;
      setSurveyDateRange({
        startDate: startDate ? startDate.toISOString() : "",
        endDate: endDate ? endDate.toISOString() : "",
      });
    },
    [setSurveyDateRange]
  );

  const handleClearAllFilters = useCallback(() => {
    setSurveySearchString(null);
    setSelectedTagIds([]);
    setSelectedFolderIds([]);
    setSelectedSiteIds([]);
    setSelectedStatuses([]);
    setSelectedOrderTypes([]);
    setSurveyDateRange({ startDate: "", endDate: "" });
    setSurveyDateType("created-date");
    setSelectedCreatedByUserIds([]);
    setSelectedLockedByUserIds([]);
    setShowOnlyArchived(false);
    if (currentFolder) {
      navigate("/folders");
    }
  }, [
    setSurveySearchString,
    setSelectedTagIds,
    setSelectedFolderIds,
    setSelectedSiteIds,
    setSelectedStatuses,
    setSelectedOrderTypes,
    setSurveyDateRange,
    setSurveyDateType,
    setSelectedCreatedByUserIds,
    setSelectedLockedByUserIds,
    setShowOnlyArchived,
    currentFolder,
    navigate,
  ]);

  const renderStatusItem: ItemRenderer<IStatus> = useCallback(
    (status, rendererProps) => {
      if (!rendererProps.modifiers.matchesPredicate) {
        return null;
      }
      const selectedStatusesList = getSelectedStatuses(
        selectedStatuses,
        statusOptions
      );
      const isSelected = selectedStatusesList.some(
        (selectedStatus: IStatus) => selectedStatus.value === status.value
      );

      return (
        <MenuItem
          {...getStatusItemProps(status, rendererProps, isSelected)}
          roleStructure="listoption"
          shouldDismissPopover={false}
          text={status.label}
        />
      );
    },
    [selectedStatuses, getSelectedStatuses]
  );

  const renderOrderTypeItem: ItemRenderer<IOrderTypeOption> = useCallback(
    (orderType, rendererProps) => {
      if (!rendererProps.modifiers.matchesPredicate) {
        return null;
      }
      const selectedOrderTypesList = getSelectedOrderTypes(
        selectedOrderTypes,
        availableOrderTypes
      );
      const isSelected = selectedOrderTypesList.some(
        (selectedOrderType: IOrderTypeOption) =>
          selectedOrderType.value === orderType.value
      );

      return (
        <MenuItem
          {...getOrderTypeItemProps(orderType, rendererProps, isSelected)}
          roleStructure="listoption"
          shouldDismissPopover={false}
          text={orderType.label}
        />
      );
    },
    [selectedOrderTypes, getSelectedOrderTypes, availableOrderTypes]
  );

  const {
    idSet: selectedCreatedByUserIdSet,
    selectedItems: selectedCreatedByUsers,
  } = useSelectedItems(selectedCreatedByUserIds, availableUsers);

  const {
    idSet: selectedLockedByUserIdSet,
    selectedItems: selectedLockedByUsers,
  } = useSelectedItems(selectedLockedByUserIds, availableUsers);

  const handleCreatedByUserRemove = useCallback(
    (user: IUser) => {
      const newUserIds = selectedCreatedByUserIds.filter(
        (id: number) => id !== user.id
      );
      setSelectedCreatedByUserIds(newUserIds);
    },
    [selectedCreatedByUserIds, setSelectedCreatedByUserIds]
  );

  const handleCreatedByUserSelect = useCallback(
    (user: IUser) => {
      if (selectedCreatedByUserIdSet.has(user.id)) {
        handleCreatedByUserRemove(user);
      } else {
        const newUserIds = [...selectedCreatedByUserIds, user.id];
        setSelectedCreatedByUserIds(newUserIds);
      }
    },
    [
      selectedCreatedByUserIds,
      selectedCreatedByUserIdSet,
      setSelectedCreatedByUserIds,
      handleCreatedByUserRemove,
    ]
  );

  const handleCreatedByUserClear = useCallback(() => {
    setSelectedCreatedByUserIds([]);
  }, [setSelectedCreatedByUserIds]);

  const handleLockedByUserRemove = useCallback(
    (user: IUser) => {
      const newUserIds = selectedLockedByUserIds.filter(
        (id: number) => id !== user.id
      );
      setSelectedLockedByUserIds(newUserIds);
    },
    [selectedLockedByUserIds, setSelectedLockedByUserIds]
  );

  const handleLockedByUserSelect = useCallback(
    (user: IUser) => {
      if (selectedLockedByUserIdSet.has(user.id)) {
        handleLockedByUserRemove(user);
      } else {
        const newUserIds = [...selectedLockedByUserIds, user.id];
        setSelectedLockedByUserIds(newUserIds);
      }
    },
    [
      selectedLockedByUserIds,
      selectedLockedByUserIdSet,
      setSelectedLockedByUserIds,
      handleLockedByUserRemove,
    ]
  );

  const handleLockedByUserClear = useCallback(() => {
    setSelectedLockedByUserIds([]);
  }, [setSelectedLockedByUserIds]);

  const renderCreatedByUserItem: ItemRenderer<IUser> = useCallback(
    (user, rendererProps) => {
      if (!rendererProps.modifiers.matchesPredicate) {
        return null;
      }
      const isSelected = selectedCreatedByUserIdSet.has(user.id);

      return (
        <MenuItem
          {...getUserItemProps(user, rendererProps, isSelected)}
          roleStructure="listoption"
          shouldDismissPopover={false}
          text={user.name}
        />
      );
    },
    [selectedCreatedByUserIdSet]
  );

  const renderLockedByUserItem: ItemRenderer<IUser> = useCallback(
    (user, rendererProps) => {
      if (!rendererProps.modifiers.matchesPredicate) {
        return null;
      }
      const isSelected = selectedLockedByUserIdSet.has(user.id);

      return (
        <MenuItem
          {...getUserItemProps(user, rendererProps, isSelected)}
          roleStructure="listoption"
          shouldDismissPopover={false}
          text={user.name}
        />
      );
    },
    [selectedLockedByUserIdSet]
  );

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        overflow: hidden;
      `}
    >
      <div
        css={css`
          display: flex;
          flex-wrap: nowrap;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          flex-shrink: 0;
          min-width: 0;
          background-color: var(--twilight);
          border-top: 1px solid var(--neutral-6);
          border-bottom: 1px solid var(--neutral-6);
        `}
      >
        <div
          css={css`
            display: flex;
            flex-wrap: nowrap;
            align-items: center;
            gap: 0.5rem;
            flex: 1;
            min-width: 0;
          `}
        >
          <div
            css={css`
              flex-shrink: 0;
            `}
          >
            <Button
              intent="primary"
              icon="map-create"
              text="Create Survey"
              onClick={() => setShowCreateDialog(true)}
            />
          </div>
          <div
            css={css`
              max-width: 240px;
              flex-shrink: 0;
            `}
          >
            <InputGroup
              placeholder="Search"
              value={surveySearchString || ""}
              onChange={(e) => setSurveySearchString(e.target.value)}
              leftIcon="search"
            />
          </div>

          <div
            css={css`
              display: flex;
              flex-wrap: nowrap;
              gap: 0.5rem;
              align-items: center;
              min-width: 0;
              overflow-x: auto;
              flex-shrink: 1;
            `}
          >
            <div
              css={css`
                flex-shrink: 0;
              `}
            >
              <Popover
                placement="bottom-start"
                isOpen={isFilterOpen}
                onInteraction={setIsFilterOpen}
                hasBackdrop
                backdropProps={{ style: { backgroundColor: "transparent" } }}
                content={
                  <div
                    css={css`
                      width: 320px;
                      max-height: 80vh;
                      overflow-y: auto;
                      padding: 1rem;
                      display: flex;
                      flex-direction: column;
                      gap: 1rem;
                    `}
                  >
                    <div
                      css={css`
                        display: flex;
                        flex-direction: column;
                        gap: 0.5rem;
                      `}
                    >
                      <h6
                        css={css`
                          color: var(--neutral-3);
                          font-size: 0.625rem;
                        `}
                      >
                        DATE
                      </h6>
                      <HTMLSelect
                        options={[
                          {
                            label: "Created Date",
                            value: "created-date",
                          },
                          {
                            label: "Data Collected Date",
                            value: "flight-date",
                          },
                          {
                            label: "Locked Date",
                            value: "locked-date",
                          },
                          {
                            label: "Order Submitted Date",
                            value: "submitted-date",
                          },
                          {
                            label: "Order Completed Date",
                            value: "completed-date",
                          },
                        ]}
                        value={surveyDateType}
                        onChange={(e) =>
                          setSurveyDateType(e.target.value as SurveyDateType)
                        }
                        fill
                      />
                      <DateRangeInput
                        fill
                        value={[
                          surveyDateRange.startDate
                            ? new Date(surveyDateRange.startDate)
                            : null,
                          surveyDateRange.endDate
                            ? new Date(surveyDateRange.endDate)
                            : null,
                        ]}
                        onChange={handleDateRangeChange}
                      />
                    </div>

                    <div
                      css={css`
                        display: flex;
                        flex-direction: column;
                        gap: 0.5rem;
                      `}
                    >
                      <h6
                        css={css`
                          color: var(--neutral-3);
                          font-size: 0.625rem;
                        `}
                      >
                        FOLDERS
                      </h6>
                      <MultiSelect
                        items={folders}
                        selectedItems={getSelectedFolders(
                          currentFolderId &&
                            !selectedFolderIds.includes(currentFolderId)
                            ? [...selectedFolderIds, currentFolderId]
                            : selectedFolderIds,
                          folders
                        )}
                        itemRenderer={renderFolderItem}
                        itemPredicate={filterFolder}
                        itemsEqual={areFoldersEqual}
                        noResults={noResults}
                        onItemSelect={handleFolderSelect}
                        onRemove={handleFolderFilterRemove}
                        onClear={handleFolderClear}
                        placeholder="Filter by folders..."
                        disabled={!folders || folders.length === 0}
                        popoverProps={{
                          minimal: true,
                          matchTargetWidth: true,
                        }}
                        tagRenderer={renderFolder}
                        tagInputProps={{
                          tagProps: {
                            minimal: false,
                          },
                        }}
                        resetOnSelect={true}
                      />
                    </div>

                    <div
                      css={css`
                        display: flex;
                        flex-direction: column;
                        gap: 0.5rem;
                      `}
                    >
                      <h6
                        css={css`
                          color: var(--neutral-3);
                          font-size: 0.625rem;
                        `}
                      >
                        SITES
                      </h6>
                      <MultiSelect
                        items={sites}
                        selectedItems={getSelectedSites(selectedSiteIds, sites)}
                        itemRenderer={renderSiteItem}
                        itemPredicate={filterSite}
                        itemsEqual={areSitesEqual}
                        noResults={noResults}
                        onItemSelect={handleSiteSelect}
                        onRemove={handleSiteRemove}
                        onClear={handleSiteClear}
                        placeholder="Filter by sites..."
                        disabled={!sites || sites.length === 0}
                        popoverProps={{
                          minimal: true,
                          matchTargetWidth: true,
                        }}
                        tagRenderer={renderSite}
                        tagInputProps={{
                          tagProps: {
                            minimal: false,
                          },
                        }}
                        resetOnSelect={true}
                      />
                    </div>

                    <div
                      css={css`
                        display: flex;
                        flex-direction: column;
                        gap: 0.5rem;
                      `}
                    >
                      <h6
                        css={css`
                          color: var(--neutral-3);
                          font-size: 0.625rem;
                        `}
                      >
                        TAGS
                      </h6>
                      <MultiSelect
                        items={tags}
                        selectedItems={getSelectedTags(selectedTagIds, tags)}
                        itemRenderer={renderTagItem}
                        itemPredicate={filterTag}
                        itemsEqual={areTagsEqual}
                        noResults={noResults}
                        onItemSelect={handleTagSelect}
                        onRemove={handleTagRemove}
                        onClear={handleTagClear}
                        placeholder="Filter by tags..."
                        disabled={!tags || tags.length === 0}
                        popoverProps={{
                          minimal: true,
                          matchTargetWidth: true,
                        }}
                        tagRenderer={renderTag}
                        tagInputProps={{
                          tagProps: {
                            minimal: false,
                          },
                        }}
                        resetOnSelect={true}
                      />
                    </div>
                    <div
                      css={css`
                        display: flex;
                        flex-direction: column;
                        gap: 0.5rem;
                      `}
                    >
                      <h6
                        css={css`
                          color: var(--neutral-3);
                          font-size: 0.625rem;
                        `}
                      >
                        STATUS
                      </h6>
                      <MultiSelect
                        items={statusOptions}
                        selectedItems={getSelectedStatuses(
                          selectedStatuses,
                          statusOptions
                        )}
                        itemRenderer={renderStatusItem}
                        itemPredicate={filterStatus}
                        itemsEqual={areStatusesEqual}
                        noResults={noResults}
                        onItemSelect={handleStatusSelect}
                        onRemove={handleStatusRemove}
                        onClear={handleStatusClear}
                        placeholder="Filter by status..."
                        disabled={!statusOptions || statusOptions.length === 0}
                        popoverProps={{
                          minimal: true,
                          matchTargetWidth: true,
                        }}
                        tagRenderer={renderStatus}
                        tagInputProps={{
                          tagProps: {
                            minimal: false,
                          },
                        }}
                        resetOnSelect={true}
                      />
                    </div>
                    <div
                      css={css`
                        display: flex;
                        flex-direction: column;
                        gap: 0.5rem;
                      `}
                    >
                      <h6
                        css={css`
                          color: var(--neutral-3);
                          font-size: 0.625rem;
                        `}
                      >
                        ORDER TYPE
                      </h6>
                      <MultiSelect
                        items={availableOrderTypes}
                        selectedItems={getSelectedOrderTypes(
                          selectedOrderTypes,
                          availableOrderTypes
                        )}
                        itemRenderer={renderOrderTypeItem}
                        itemPredicate={filterOrderType}
                        itemsEqual={areOrderTypesEqual}
                        noResults={noResults}
                        onItemSelect={handleOrderTypeSelect}
                        onRemove={handleOrderTypeRemove}
                        onClear={handleOrderTypeClear}
                        placeholder="Filter by order type…"
                        disabled={
                          !availableOrderTypes ||
                          availableOrderTypes.length === 0
                        }
                        popoverProps={{
                          minimal: true,
                          matchTargetWidth: true,
                        }}
                        tagRenderer={renderOrderType}
                        tagInputProps={{
                          tagProps: {
                            minimal: false,
                          },
                        }}
                        resetOnSelect={true}
                      />
                    </div>
                    <div
                      css={css`
                        display: flex;
                        flex-direction: column;
                        gap: 0.5rem;
                      `}
                    >
                      <h6
                        css={css`
                          color: var(--neutral-3);
                          font-size: 0.625rem;
                        `}
                      >
                        CREATED BY
                      </h6>

                      <MultiSelect
                        items={availableUsers}
                        selectedItems={selectedCreatedByUsers}
                        itemRenderer={renderCreatedByUserItem}
                        itemListRenderer={cappedItemListRenderer}
                        itemPredicate={filterUser}
                        itemsEqual={areUsersEqual}
                        onItemSelect={handleCreatedByUserSelect}
                        onRemove={handleCreatedByUserRemove}
                        onClear={handleCreatedByUserClear}
                        placeholder="Filter by creator..."
                        disabled={
                          !availableUsers || availableUsers.length === 0
                        }
                        popoverProps={{
                          minimal: true,
                          matchTargetWidth: true,
                        }}
                        tagRenderer={renderUser}
                        tagInputProps={{
                          tagProps: {
                            minimal: false,
                          },
                        }}
                        resetOnSelect={true}
                      />
                    </div>
                    <div
                      css={css`
                        display: flex;
                        flex-direction: column;
                        gap: 0.5rem;
                      `}
                    >
                      <h6
                        css={css`
                          color: var(--neutral-3);
                          font-size: 0.625rem;
                        `}
                      >
                        LOCKED BY
                      </h6>

                      <MultiSelect
                        items={availableUsers}
                        selectedItems={selectedLockedByUsers}
                        itemRenderer={renderLockedByUserItem}
                        itemListRenderer={cappedItemListRenderer}
                        itemPredicate={filterUser}
                        itemsEqual={areUsersEqual}
                        onItemSelect={handleLockedByUserSelect}
                        onRemove={handleLockedByUserRemove}
                        onClear={handleLockedByUserClear}
                        placeholder="Filter by locker..."
                        disabled={
                          !availableUsers || availableUsers.length === 0
                        }
                        popoverProps={{
                          minimal: true,
                          matchTargetWidth: true,
                        }}
                        tagRenderer={renderUser}
                        tagInputProps={{
                          tagProps: {
                            minimal: false,
                          },
                        }}
                        resetOnSelect={true}
                      />
                    </div>
                    <div
                      css={css`
                        display: flex;
                        flex-direction: column;
                        gap: 0.5rem;
                      `}
                    >
                      <h6
                        css={css`
                          color: var(--neutral-3);
                          font-size: 0.625rem;
                        `}
                      >
                        ARCHIVED
                      </h6>
                      <Switch
                        css={css`
                          margin: 0;
                        `}
                        label="Only show archived surveys"
                        checked={showOnlyArchived}
                        onChange={(e) =>
                          setShowOnlyArchived(e.currentTarget.checked)
                        }
                      />
                    </div>
                    <Button
                      icon="cross"
                      text="Clear Filters"
                      size="small"
                      onClick={() => {
                        handleClearAllFilters();
                        setIsFilterOpen(false);
                      }}
                    />
                  </div>
                }
              >
                <Button
                  icon="filter"
                  endIcon="caret-down"
                  text="Filter"
                  intent={hasActiveFilters ? "primary" : undefined}
                  onClick={() => setIsFilterOpen((open) => !open)}
                />
              </Popover>
            </div>

            {currentFolder && (
              <FilterChip onRemove={handleFolderRemove} header="Folder">
                {currentFolder.label}
              </FilterChip>
            )}

            {surveySearchString && (
              <FilterChip onRemove={handleSearchRemove} header="Search">
                {surveySearchString}
              </FilterChip>
            )}

            {surveyDateRange.startDate && surveyDateRange.endDate && (
              <FilterChip
                onRemove={handleDateRangeRemove}
                header={SURVEY_DATE_TYPE_LABELS[surveyDateType]}
              >
                {formatDateForDisplay(surveyDateRange.startDate)} -{" "}
                {formatDateForDisplay(surveyDateRange.endDate)}
              </FilterChip>
            )}

            {getSelectedSites(selectedSiteIds, sites).map((site: ISite) => (
              <FilterChip
                key={`applied-site-${site.id}`}
                onRemove={() => handleSiteRemove(site)}
                header="Site"
              >
                {site.label}
              </FilterChip>
            ))}

            {getSelectedFolders(
              selectedFolderIds.filter((id) => id !== currentFolderId),
              folders
            ).map((folder: IFolder) => (
              <FilterChip
                key={`applied-folder-${folder.id}`}
                onRemove={() => handleFolderFilterRemove(folder)}
                header="Folder"
              >
                {folder.label}
              </FilterChip>
            ))}

            {selectedCreatedByUsers.map((user: IUser) => (
              <FilterChip
                key={`applied-created-by-user-${user.id}`}
                onRemove={() => handleCreatedByUserRemove(user)}
                header="Created by"
              >
                {user.name}
              </FilterChip>
            ))}

            {selectedLockedByUsers.map((user: IUser) => (
              <FilterChip
                key={`applied-locked-by-user-${user.id}`}
                onRemove={() => handleLockedByUserRemove(user)}
                header="Locked by"
              >
                {user.name}
              </FilterChip>
            ))}

            {getSelectedTags(selectedTagIds, tags).map((tag: ITag) => (
              <FilterChip
                key={`applied-tag-${tag.id}`}
                onRemove={() => handleTagRemove(tag)}
                header="Tag"
              >
                {tag.label}
              </FilterChip>
            ))}

            {getSelectedStatuses(selectedStatuses, statusOptions).map(
              (status: IStatus) => (
                <FilterChip
                  key={`applied-status-${status.value}`}
                  onRemove={() => handleStatusRemove(status)}
                  header="Status"
                >
                  {status.label}
                </FilterChip>
              )
            )}

            {getSelectedOrderTypes(selectedOrderTypes, availableOrderTypes).map(
              (orderType: IOrderTypeOption) => (
                <FilterChip
                  key={`applied-order-type-${orderType.value}`}
                  onRemove={() => handleOrderTypeRemove(orderType)}
                  header="Order type"
                >
                  {orderType.label}
                </FilterChip>
              )
            )}

            {showOnlyArchived && (
              <FilterChip
                header="Archived"
                onRemove={() => setShowOnlyArchived(false)}
              >
                Only archived
              </FilterChip>
            )}
          </div>
        </div>
        <div
          css={css`
            display: flex;
            flex-wrap: nowrap;
            align-items: center;
            gap: 0.5rem;
            flex-shrink: 0;
          `}
        >
          {filteredSurveys.length > 0 && !isFolderListView && (
            <ButtonGroup>
              <Button
                icon="expand-all"
                text="Expand All"
                onClick={() => expandAllSurveys(visibleSurveyIdsRef.current)}
              />
              <Button
                icon="collapse-all"
                text="Collapse All"
                onClick={() => collapseAllSurveys()}
              />
            </ButtonGroup>
          )}
          {isFolderListView && (
            <FolderAdminReactivatePopover onReactivated={resink} />
          )}
        </div>
      </div>
      {isFolderListView && <FoldersView />}
      {location.pathname === "/folders" && hasActiveFilters && (
        <SurveysView
          onCreateSurvey={() => setShowCreateDialog(true)}
          onVisibleSurveyIdsChange={handleVisibleSurveyIdsChange}
          surveys={surveys ?? []}
          folderFilteredSurveys={folderFilteredSurveys}
          filteredSurveys={filteredSurveys}
          hasActiveFilters={hasActiveFilters}
        />
      )}
      {location.pathname === "/surveys" && (
        <SurveysView
          onCreateSurvey={() => setShowCreateDialog(true)}
          onVisibleSurveyIdsChange={handleVisibleSurveyIdsChange}
          surveys={surveys ?? []}
          folderFilteredSurveys={folderFilteredSurveys}
          filteredSurveys={filteredSurveys}
          hasActiveFilters={hasActiveFilters}
        />
      )}
      {location.pathname.startsWith("/folders/") &&
        params.id &&
        params.id !== "all" && (
          <SurveysView
            onCreateSurvey={() => setShowCreateDialog(true)}
            onVisibleSurveyIdsChange={handleVisibleSurveyIdsChange}
            surveys={surveys ?? []}
            folderFilteredSurveys={folderFilteredSurveys}
            filteredSurveys={filteredSurveys}
            hasActiveFilters={hasActiveFilters}
          />
        )}
      <CreateSurvey
        show={showCreateDialog}
        hide={() => setShowCreateDialog(false)}
      />
    </div>
  );
};

export default SurveysNavigator;
